import { apiClient } from '@/services/shared/apiClient';
import {
  WorkOrder,
  WorkOrderMaterial,
  MaintenanceRequest,
  WorkOrderTimeEntry,
} from '@/hooks/entities/admin/useWorkOrders';

interface WorkOrderComment {
  id: string;
  tenantId: string;
  workOrderId: string;
  authorId: string;
  authorName: string;
  commentText: string;
  commentType: 'note' | 'status_update' | 'question' | 'solution';
  isInternal: boolean;
  attachments: string[];
  createdAt: string;
  updatedAt: string;
}

class WorkOrderService {
  // Work Orders
  async getWorkOrders(
    tenantId?: string,
    filters?: {
      priority?: string;
      status?: string;
      category?: string;
      assignedTo?: string;
      location?: string;
    },
  ): Promise<WorkOrder[]> {
    return apiClient.get<WorkOrder[]>('/work-orders', {
      tenantId,
      ...filters,
    });
  }

  async getWorkOrder(workOrderId: string): Promise<WorkOrder> {
    return apiClient.get<WorkOrder>(`/work-orders/${workOrderId}`);
  }

  async createWorkOrder(
    tenantId: string,
    workOrderData: Omit<
      WorkOrder,
      | 'id'
      | 'tenantId'
      | 'workOrderNumber'
      | 'status'
      | 'completionPercentage'
      | 'contractorRequired'
      | 'contractorInsuranceVerified'
      | 'petsOnPremises'
      | 'warrantyPeriodMonths'
      | 'followUpRequired'
      | 'followUpCompleted'
      | 'residentNotified'
      | 'completionNotificationSent'
      | 'relatedWorkOrders'
      | 'createdAt'
      | 'updatedAt'
    >,
  ): Promise<WorkOrder> {
    return apiClient.post('/work-orders', { tenantId, ...workOrderData });
  }

  async updateWorkOrder(workOrderId: string, updates: Partial<WorkOrder>): Promise<WorkOrder> {
    return apiClient.put<WorkOrder>(`/work-orders/${workOrderId}`, updates);
  }

  async deleteWorkOrder(workOrderId: string): Promise<void> {
    await apiClient.delete(`/work-orders/${workOrderId}`);
  }

  async updateWorkOrderStatus(
    workOrderId: string,
    status: string,
    notes?: string,
  ): Promise<WorkOrder> {
    return apiClient.put<WorkOrder>(`/work-orders/${workOrderId}/status`, {
      status,
      statusNotes: notes,
    });
  }

  // Comments
  async getWorkOrderComments(workOrderId: string): Promise<WorkOrderComment[]> {
    return apiClient.get<WorkOrderComment[]>(`/work-orders/${workOrderId}/comments`);
  }

  async addWorkOrderComment(
    workOrderId: string,
    commentData: Omit<WorkOrderComment, 'id' | 'workOrderId' | 'createdAt' | 'updatedAt'>,
  ): Promise<WorkOrderComment> {
    return apiClient.post<WorkOrderComment>(`/work-orders/${workOrderId}/comments`, commentData);
  }

  async updateWorkOrderComment(
    commentId: string,
    updates: Partial<WorkOrderComment>,
  ): Promise<WorkOrderComment> {
    return apiClient.put<WorkOrderComment>(`/work-orders/comments/${commentId}`, updates);
  }

  async deleteWorkOrderComment(commentId: string): Promise<void> {
    await apiClient.delete(`/work-orders/comments/${commentId}`);
  }

  // Materials
  async getWorkOrderMaterials(workOrderId: string): Promise<WorkOrderMaterial[]> {
    return apiClient.get<WorkOrderMaterial[]>(`/work-orders/${workOrderId}/materials`);
  }

  async addWorkOrderMaterial(
    workOrderId: string,
    materialData: Omit<
      WorkOrderMaterial,
      'id' | 'workOrderId' | 'totalCost' | 'createdAt' | 'updatedAt'
    >,
  ): Promise<WorkOrderMaterial> {
    return apiClient.post<WorkOrderMaterial>(`/work-orders/${workOrderId}/materials`, materialData);
  }

  async updateWorkOrderMaterial(
    materialId: string,
    updates: Partial<WorkOrderMaterial>,
  ): Promise<WorkOrderMaterial> {
    return apiClient.put<WorkOrderMaterial>(`/work-orders/materials/${materialId}`, updates);
  }

  async deleteWorkOrderMaterial(materialId: string): Promise<void> {
    await apiClient.delete(`/work-orders/materials/${materialId}`);
  }

  // Time Entries
  async getWorkOrderTimeEntries(workOrderId: string): Promise<WorkOrderTimeEntry[]> {
    return apiClient.get<WorkOrderTimeEntry[]>(`/work-orders/${workOrderId}/time-entries`);
  }

  async addWorkOrderTimeEntry(
    workOrderId: string,
    timeData: Omit<WorkOrderTimeEntry, 'id' | 'workOrderId' | 'createdAt' | 'updatedAt'>,
  ): Promise<WorkOrderTimeEntry> {
    return apiClient.post<WorkOrderTimeEntry>(`/work-orders/${workOrderId}/time-entries`, timeData);
  }

  async updateWorkOrderTimeEntry(
    timeEntryId: string,
    updates: Partial<WorkOrderTimeEntry>,
  ): Promise<WorkOrderTimeEntry> {
    return apiClient.put<WorkOrderTimeEntry>(`/work-orders/time-entries/${timeEntryId}`, updates);
  }

  async deleteWorkOrderTimeEntry(timeEntryId: string): Promise<void> {
    await apiClient.delete(`/work-orders/time-entries/${timeEntryId}`);
  }

  // Work Order Reports
  async getWorkOrderReport(filters?: {
    startDate?: string;
    endDate?: string;
    status?: string;
    priority?: string;
    category?: string;
    assignedTo?: string;
  }): Promise<{
    totalWorkOrders: number;
    openWorkOrders: number;
    completedWorkOrders: number;
    averageCompletionTime: number;
    totalCost: number;
    totalHours: number;
    workOrdersByCategory: Record<string, number>;
    workOrdersByPriority: Record<string, number>;
    workOrdersByStatus: Record<string, number>;
  }> {
    return apiClient.get('/work-orders/reports', filters);
  }

  // Bulk Operations
  async bulkUpdateWorkOrders(
    workOrderIds: string[],
    updates: Partial<WorkOrder>,
  ): Promise<WorkOrder[]> {
    return apiClient.put<WorkOrder[]>('/work-orders/bulk-update', {
      workOrderIds,
      updates,
    });
  }

  async exportWorkOrders(filters?: Record<string, unknown>): Promise<Blob> {
    const response = await fetch(`${this.baseUrl}/work-orders/export`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.getAuthToken()}`,
      },
      body: JSON.stringify(filters || {}),
    });

    if (!response.ok) {
      throw new Error(`Export failed: ${response.status} ${response.statusText}`);
    }

    return response.blob();
  }

  private get baseUrl() {
    return import.meta.env.VITE_API_BASE_URL || '/api';
  }

  private getAuthToken(): string {
    return localStorage.getItem('authToken') || '';
  }

  // Additional methods expected by hooks
  async uploadPhotos(
    workOrderId: string,
    files: File[],
    type: 'before' | 'during' | 'after',
  ): Promise<string[]> {
    const formData = new FormData();
    files.forEach((file) => formData.append('photos', file));
    formData.append('type', type);

    const response = await apiClient.post<{ urls: string[] }>(
      `/work-orders/${workOrderId}/photos`,
      formData,
    );
    return response.urls;
  }

  // Maintenance Requests
  async getMaintenanceRequests(tenantId?: string, userId?: string): Promise<MaintenanceRequest[]> {
    return apiClient.get('/maintenance-requests', { tenantId, userId });
  }

  async createMaintenanceRequest(
    tenantId: string,
    requestData: Omit<
      MaintenanceRequest,
      'id' | 'tenantId' | 'status' | 'attachments' | 'createdAt' | 'updatedAt'
    >,
  ): Promise<MaintenanceRequest> {
    return apiClient.post('/maintenance-requests', { tenantId, ...requestData });
  }

  async convertRequestToWorkOrder(requestId: string, tenantId?: string): Promise<WorkOrder> {
    return apiClient.post<WorkOrder>(`/maintenance-requests/${requestId}/convert`, { tenantId });
  }

  // Material management alias methods
  async addMaterial(
    workOrderId: string,
    materialData: Omit<
      WorkOrderMaterial,
      | 'id'
      | 'tenantId'
      | 'workOrderId'
      | 'quantityOrdered'
      | 'quantityReceived'
      | 'quantityUsed'
      | 'quantityReturned'
      | 'status'
      | 'createdAt'
      | 'updatedAt'
    >,
  ): Promise<WorkOrderMaterial> {
    return apiClient.post<WorkOrderMaterial>(`/work-orders/${workOrderId}/materials`, materialData);
  }

  // Time tracking alias methods
  async getTimeEntries(workOrderId: string): Promise<WorkOrderTimeEntry[]> {
    return this.getWorkOrderTimeEntries(workOrderId);
  }

  async startTimeTracking(
    workOrderId: string,
    userId: string,
    description?: string,
  ): Promise<WorkOrderTimeEntry> {
    return this.addWorkOrderTimeEntry(workOrderId, {
      tenantId: '',
      workerId: userId,
      activityDescription: description || 'Time tracking started',
      startTime: new Date().toISOString(),
      status: 'active',
      breakTimeMinutes: 0,
    });
  }

  async stopTimeTracking(timeEntryId: string): Promise<WorkOrderTimeEntry> {
    return this.updateWorkOrderTimeEntry(timeEntryId, {
      endTime: new Date().toISOString(),
      status: 'completed',
    });
  }
}

export const workOrderService = new WorkOrderService();
